import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Input, TextArea } from '@/components_deprecated'
import React from 'react'
import { isSafari } from 'react-device-detect'
import styled, { css } from 'styled-components'
import type { UserEquipmentTypesSettingsViewProps } from './types'

interface WrapperProps {
  isSafari: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${props =>
    props.isSafari &&
    css`
      > * {
        transform: translateZ(0);
      }
    `}
`

const EquipmentType = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.gray3};
  border-radius: 0.5rem;

  position: relative;

  .delete {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  p {
    margin: 0.25rem 0 0 0;
    color: ${props => props.theme.colors.gray6};
    font-size: 0.875rem;
  }

  .num-users {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: ${props => props.theme.colors.gray5};
  }
`

const EditName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  .cancel,
  .submit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.25rem;
    cursor: pointer;

    &:hover {
      background-color: ${props => props.theme.colors.gray2};
    }
  }
`

const NameInput = styled(Input).attrs({
  width: 'calc(260px - 5rem)',
  height: '28px',
  noBorders: true,
})`
  margin: -7px 0 0 -4px;

  input {
    font-size: 1rem;
    font-weight: 500;
    background-color: rgba(255, 255, 255, 0.4);
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  ${props => props.theme.media.mobile} {
    width: calc(100vw - 12rem);

    input {
      width: calc(100vw - 12rem);
    }
  }
`

const DescriptionInput = styled(TextArea).attrs({
  width: 'calc(260px - 5rem)',
  height: '60px',
  noBorders: true,
})`
  margin: -7px 0 0 -4px;

  textarea {
    font-size: 0.875rem;
    background-color: rgba(255, 255, 255, 0.4);
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  ${props => props.theme.media.mobile} {
    width: calc(100vw - 12rem);

    textarea {
      width: calc(100vw - 12rem);
    }
  }
`

interface NewEquipmentTypeProps {
  adding: boolean
}

const NewEquipmentType = styled(EquipmentType)<NewEquipmentTypeProps>`
  ${props =>
    !props.adding
      ? css`
          justify-content: center;
          align-items: center;

          font-weight: 600;
          user-select: none;
          cursor: pointer;
          border: 1px dashed ${props => props.theme.colors.secondary};

          &:hover {
            color: white;
            background-color: ${props => props.theme.colors.secondary};
            border: 0;
          }
        `
      : css`
          ${NameInput} {
            width: calc(100% - 1.8rem) !important;
          }
          ${DescriptionInput} {
            width: calc(100% - 1.8rem) !important;
          }
        `};
`

interface UserEquipmentTypeSettingsCardProps
  extends UserEquipmentTypesSettingsViewProps {}

export const UserEquipmentTypeSettingsCard: React.FC<
  UserEquipmentTypeSettingsCardProps
> = ({
  editableTypes,
  form,
  adding,
  editing,
  onFormChange,
  onUpdateForm,
  onAdd,
  // TODO unused?
  onAddSubmit: _onAddSubmit,
  onEdit,
  onEditSubmit,
  onDelete,
}) => {
  const translations = useTranslate({
    add: 'common.add',
    choose: 'common.choose-ellipsis',
    createNameFirst: 'settings.create-name-first',
  })

  function handleEditKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
    if (evt.key === 'Enter') onEditSubmit()
    else if (evt.key === 'Escape') onEdit(null)
  }

  return (
    <Wrapper isSafari={isSafari}>
      {editableTypes.map(type => (
        <EquipmentType key={type.id}>
          <Icon
            className="delete"
            icon="times"
            color="gray3"
            hoverColor="red"
            cursor="pointer"
            onClick={() => onDelete(type)}
          />

          {editing !== type.id ? (
            <div>
              <h3>
                {type.name}
                {type.editable && (
                  <Icon
                    icon="pen"
                    type="solid"
                    color="gray3"
                    size="0.8rem"
                    hoverColor="secondary"
                    cursor="pointer"
                    margin="0 0 0 6px"
                    translateY="-1px"
                    onClick={() => {
                      onUpdateForm({
                        editName: type.name,
                        editDescription: type.description,
                      })
                      onEdit(type.id)
                    }}
                  />
                )}
              </h3>
              <p>{type.description}</p>
            </div>
          ) : (
            <EditName>
              <div>
                <NameInput
                  value={form.editName}
                  autoFocus
                  onFocus={evt => evt.target.select()}
                  onKeyDown={handleEditKeyDown}
                  onChange={onFormChange('editName')}
                />
                <DescriptionInput
                  value={form.editDescription}
                  onChange={onFormChange('editDescription')}
                />
              </div>

              <div
                className="cancel"
                role="button"
                onClick={evt => {
                  evt.stopPropagation()
                  onEdit(null)
                }}
              >
                <Icon icon="times" type="light" size="1rem" color="gray2" />
              </div>

              <div
                className="submit"
                role="button"
                onClick={evt => {
                  evt.stopPropagation()
                  onEditSubmit()
                }}
              >
                <Icon icon="check" type="light" size="1rem" color="gray2" />
              </div>
            </EditName>
          )}
        </EquipmentType>
      ))}

      <NewEquipmentType adding={adding}>
        {!adding ? (
          <div onClick={() => onAdd(true)}>
            <Icon icon="plus" margin="0 0.5rem 0 0" />
            {translations.add}
          </div>
        ) : (
          <div>
            <NameInput
              value={form.newName}
              autoFocus
              onFocus={evt => evt.target.select()}
              onChange={onFormChange('newName')}
              placeholder={translations.createNameFirst}
            />
            <DescriptionInput
              value={form.newDescription}
              onChange={onFormChange('newDescription')}
              placeholder="Description (optional)"
            />
          </div>
        )}
      </NewEquipmentType>
    </Wrapper>
  )
}
